import {
  BellRing,
  BellOff,
  CalendarClock,
  Edit3,
  FileText,
  Trash2,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { policyPdfUrl } from "../../lib/api";
import {
  LIFE_LIKE_TYPES,
  daysUntil,
  formatDate,
  formatINR,
  freqLabel,
  typeMeta,
} from "./policyShared";

/**
 * Single policy card — extracted from PoliciesPage.jsx during 08-Feb refactor. Behaviour is
 * unchanged; the goal was to shrink the parent file and give this component (~130 LoC) its own
 * home so future changes (e.g., inline surrender-value analysis) can happen here without
 * touching the page shell.
 *
 * Props:
 *   - policy: full API policy object
 *   - onEdit / onDelete: callbacks invoked by the icon buttons
 */
export function PolicyCard({ policy, onEdit, onDelete }) {
  const days = daysUntil(policy.next_renewal_date);
  const renewalSoon = days != null && days >= 0 && days <= 30;
  const renewalOverdue = days != null && days < 0;
  const meta = typeMeta(policy.type);

  return (
    <Card
      className="group relative overflow-hidden border-border transition-all hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[0_10px_30px_-15px_rgba(212,175,55,0.4)]"
      data-testid={`policy-card-${policy.id}`}
    >
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-gold/80">{meta.label}</div>
            <div className="mt-1 truncate font-serif text-lg text-text">{policy.name}</div>
            <div className="truncate text-xs text-muted">{policy.insurer || "—"}</div>
          </div>
          <div className="flex flex-shrink-0 gap-1">
            <button
              onClick={onEdit}
              data-testid={`policy-edit-${policy.id}`}
              className="rounded-md border border-border p-1.5 text-muted hover:border-gold hover:text-gold"
              aria-label="Edit"
            >
              <Edit3 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onDelete}
              data-testid={`policy-delete-${policy.id}`}
              className="rounded-md border border-border p-1.5 text-muted hover:border-red-500 hover:text-red-400"
              aria-label="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <StatRow label="Sum assured" value={formatINR(policy.sum_assured)} accent />
          <StatRow label="Premium" value={formatINR(policy.premium_amount)} />
          <StatRow label="Frequency" value={freqLabel(policy.premium_frequency)} />
          <StatRow
            label={LIFE_LIKE_TYPES.has(policy.type) ? "Policy / PPT" : "Term"}
            value={
              policy.policy_term_years || policy.premium_paying_term_years
                ? `${policy.policy_term_years ?? "—"} / ${policy.premium_paying_term_years ?? "—"} yr`
                : "—"
            }
          />
          {policy.type === "life_insurance" && (
            <>
              <StatRow label="Plan #" value={policy.plan_number || "—"} />
              <StatRow label="Maturity" value={formatDate(policy.maturity_date)} accent />
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-xs">
          <div className="flex items-center gap-1.5 text-muted">
            <CalendarClock className="h-3.5 w-3.5 text-gold/70" />
            <span>Next renewal: {formatDate(policy.next_renewal_date)}</span>
          </div>
          {renewalOverdue && (
            <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-red-400">
              Overdue {Math.abs(days)}d
            </span>
          )}
          {renewalSoon && (
            <span
              className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-300"
              data-testid={`policy-renewal-soon-${policy.id}`}
            >
              Due in {days}d
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[10px] text-muted">
            {policy.reminder_enabled ? (
              <>
                <BellRing className="h-3 w-3 text-gold" />
                {policy.reminder_channel === "none"
                  ? "Reminder on"
                  : `${policy.reminder_channel} reminder`}
              </>
            ) : (
              <>
                <BellOff className="h-3 w-3" /> No reminder
              </>
            )}
          </span>
          {policy.has_pdf && (
            <a
              href={policyPdfUrl(policy.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-full border border-gold/30 bg-gold/5 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold hover:bg-gold/15"
              data-testid={`policy-pdf-link-${policy.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <FileText className="h-3 w-3" /> PDF
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatRow({ label, value, accent = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
      <div className={accent ? "font-medium text-gold" : "text-text"}>{value}</div>
    </div>
  );
}
